import React, { FC, useCallback, useEffect, useState } from "react";

import { BaseColorPickerModal, IShimmerColors } from "./BaseColorPickerModal";
import {
  firstHexFromGradient,
  isGradient,
  isValidGradient,
  lightOrDark,
  normalizeHex,
} from "utils/colorUtils";
import { useColorState, useShadeFromColor } from "./useColorPickerState";

type ColorType = "primary" | "secondary";

export interface ISingleColorPickerModalProps {
  title?: string;
  description?: string;
  /** Current value: a 6-hex without `#`, a `linear-gradient(...)` string, or "". */
  defaultColor: string;
  /** The other slot's color (6-hex) — drives the mini-card preview. */
  otherColor: string;
  colorType: ColorType;
  isOpen: boolean;
  onClose: () => void;
  /** Saves a 6-hex without `#`, or — when a gradient is entered — the raw gradient. */
  onSave: (value: string) => void;
  /** Show the "Gradient code" field (the card-background / Primary slot). */
  allowGradient?: boolean;
}

export const SingleColorPickerModal: FC<ISingleColorPickerModalProps> = ({
  title = "Select color",
  description = "Pick a color for your profile.",
  defaultColor,
  otherColor,
  colorType,
  isOpen,
  onClose,
  onSave,
  allowGradient = false,
}) => {
  const hasGradientDefault = allowGradient && isGradient(defaultColor);
  // When the saved value is a gradient the solid picker can't represent it, so
  // seed it from the gradient's first color (not black) — reopening the modal
  // then shows a hex that matches the gradient rather than #000000.
  const solidDefault = hasGradientDefault
    ? firstHexFromGradient(defaultColor) || "000000"
    : defaultColor || "000000";

  const { color, handleColorChange, resetToDefault } = useColorState(
    solidDefault,
    isOpen,
  );
  const [gradient, setGradient] = useState("");

  useEffect(() => {
    if (isOpen) {
      setGradient(hasGradientDefault ? defaultColor : "");
    }
  }, [isOpen, defaultColor, hasGradientDefault]);

  const otherShade = useShadeFromColor(otherColor);
  const normalizedOther = `#${otherColor.replace("#", "")}`;

  const gradientEntered = allowGradient && gradient.trim().length > 0;
  // The picker accepts 3-digit shorthand (#abc) but the backend rejects
  // anything that isn't exactly 6 hex digits — expand/normalize before saving
  // so a valid pick can't 400 on the wire. "" means the hex is invalid.
  const normalizedColor = normalizeHex(color);
  const primaryDisplay = gradientEntered ? gradient : color;
  const primaryShade = lightOrDark(primaryDisplay) || "light";
  const shade = lightOrDark(color) || "light";

  const shimmerColors: IShimmerColors =
    colorType === "primary"
      ? {
          primaryColor: primaryDisplay,
          secondaryColor: normalizedOther,
          primaryShade,
          secondaryShade: otherShade,
        }
      : {
          primaryColor: normalizedOther,
          secondaryColor: color,
          primaryShade: otherShade,
          secondaryShade: shade,
        };

  // Editing the solid picker clears a pasted gradient (the user is choosing a
  // solid color) — keeps the card background a single value (last-write-wins).
  const handlePickerChange = useCallback(
    (next: string) => {
      handleColorChange(next);
      setGradient(prev => (prev ? "" : prev));
    },
    [handleColorChange],
  );

  const handleSave = useCallback(() => {
    onSave(gradientEntered ? gradient.trim() : normalizedColor);
    onClose();
  }, [gradientEntered, gradient, normalizedColor, onSave, onClose]);

  const handleClose = useCallback(() => {
    resetToDefault();
    onClose();
  }, [resetToDefault, onClose]);

  return (
    <BaseColorPickerModal
      title={title}
      description={description}
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleSave}
      isDisabled={
        gradientEntered ? !isValidGradient(gradient) : !normalizedColor
      }
      currentColor={color}
      onColorChange={handlePickerChange}
      shimmerColors={shimmerColors}
      allowGradient={allowGradient}
      gradient={gradient}
      onGradientChange={setGradient}
    />
  );
};
